import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { LoginLanguageSwitcher } from "@/components/LoginLanguageSwitcher";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [authError, setAuthError] = useState<string | null>(null);
  const showPaymentSuccess = new URLSearchParams(location.search).get('payment_success') !== null;
  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/");
      }
    };
    
    checkUser();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
      <LoginLanguageSwitcher />
      <div className="w-full max-w-md">
        <div className="bg-white p-8 rounded-lg shadow-lg border mb-6">
          {/* Logo at the top */}
          <div className="text-center mb-6">
            <img src="/tuties.png" alt="TUTIES" className="h-[3.25rem] mx-auto" />
          </div>
          
          {/* Success message with checkbox next to title */}
          {showPaymentSuccess && (
            <div className="mb-6">
              <div className="flex items-center justify-center gap-3 mb-2">
                <CheckCircle2 className="text-green-500 w-8 h-8" />
                <h2 className="text-2xl font-bold">
                  {t('auth.payment.success')}
                </h2>
              </div>
              <h3 className="text-xl font-bold text-center mb-2">
                {t('auth.welcomeToTuties')}
              </h3>
              <p className="text-center text-muted-foreground">
                {t('auth.chooseSignInMethod')}
              </p>
            </div>
          )}
          
          {/* Regular message for non-payment success */}
          {!showPaymentSuccess && (
            <p className="text-center text-muted-foreground mb-6">
              {t('auth.createAccountMessage')}
            </p>
          )}
          
          {/* Error message */}
          {authError && (
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md mb-6">
              <p>{authError}</p>
            </div>
          )}
          
          <Auth
            supabaseClient={supabase}
            view="sign_up"
            appearance={{
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    brand: 'hsl(var(--primary))',
                    brandAccent: 'hsl(var(--primary))',
                  },
                },
              },
            }}
            localization={{
              variables: {
                sign_up: {
                  email_label: t('auth.emailAddress'),
                  password_label: t('auth.password'),
                  button_label: t('auth.signUp'),
                  loading_button_label: t('common.loading'),
                  social_provider_text: t('auth.signUpWith'),
                  link_text: t('auth.createAccount'),
                }
              },
            }}
            showLinks={false}
            providers={["google"]}
            // @ts-expect-error - onError exists in Auth component but TypeScript doesn't recognize it properly
            onError={(error) => {
              console.error('Supabase Auth Error:', error);
              
              // Handle auth errors
              if (error.message) {
                setAuthError(`Authentication error: ${error.message}`);
              }
            }}
            redirectTo={`${window.location.origin}/verify`}
          />
        </div>
      </div>
    </div>
  );
};

export default Register;
